import { noop } from 'lodash'
import GridEntity from '@/entities/grid'

/**
 * Data grid config
 * Doc: https://www.ag-grid.com/javascript-grid-row-models/
 * Examples:
 * - client-side: https://www.ag-grid.com/javascript-grid-client-side-model/
 * - infinite: https://www.ag-grid.com/javascript-grid-infinite-scrolling/
 * - server-side: https://medium.com/@sebastianvcruz/ag-grid-server-side-infinity-scroll-with-paginated-restful-api-and-react-js-b961a847ba7a
 */
const ROW_MODEL_TYPE = GridEntity.sidesData.SERVER_SIDE

/**
 * Pagination Grid
 * Enabled in the row modal types `infinite` and `serverSide`
 */
const PER_PAGE = 100
// const PAGINATION = {
//     page: 1,
//     perPage: PER_PAGE,
//     total: undefined,
//     lastPage: undefined,
// }

/**
 * Header default config
 * Enable checkbox selection and common header config
 */
const DEFAULT_COL_DEF = {
    checkboxSelection: GridEntity.isFirstCol,
}

/**
 * Logger
 */
const logger = (response, request = {}) => console.loggy(`
    ------\n
    Page ${((response || {}).meta || {}).page}\n
    Elements ${((response || {}).data || []).length}\n
    ------
`)

/**
 * Data grid config
 * Apply config by row model type
 * Doc: https://www.ag-grid.com/javascript-grid-row-models/
 */
const DATAGRID_CONFIG = {
    hasInputFilter: false,
    filterable: true,
    sideBar: true,
    defaultColDef: DEFAULT_COL_DEF,

    // Client side data
    ...(ROW_MODEL_TYPE === GridEntity.sidesData.CLIENT_SIDE ? {
        defaultColDef: {
            ...DEFAULT_COL_DEF,
            headerCheckboxSelection: GridEntity.isFirstCol,
        },
        paginable: true,
    } : {}),

    // Server side data - `infinite`
    ...(ROW_MODEL_TYPE === GridEntity.sidesData.INFINITE ? {
        rowModelType: GridEntity.sidesData.INFINITE,
        rowBuffer: 0,
        paginationPageSize: PER_PAGE,
        cacheOverflowSize: 2,
        maxConcurrentDatasourceRequests: 1,
        infiniteInitialRowCount: PER_PAGE,
        maxBlocksInCache: 10,
        components: {
            loading: (params) => {
                if (params.value !== undefined) {
                    return params.value
                }
                return '<img src="https://loading.io/mod/spinner/spinner/sample.gif" width="40" height="40">'
            },
        },
    } : {}),

    // Server side data - `server-side`
    ...(ROW_MODEL_TYPE === GridEntity.sidesData.SERVER_SIDE ? {
        rowModelType: GridEntity.sidesData.SERVER_SIDE,
        pagination: true,
        paginationPageSize: PER_PAGE,
        cacheBlockSize: PER_PAGE,
        maxBlocksInCache: 3,
    } : {}),
}

/**
 * Data grid handler
 * Manage pagination and data
 */
const Handler = ({
    fetch = noop, success = noop, error = noop, grid = {},
}) => {
    // Fetch middleware
    const fetchRequest = (params, callback) => fetch(params)
        .then((response) => {
            callback(response)
            logger(response)
            success(response)
        })
        .catch(error)
    // Server side handler
    const dataSource = {
        async getRows ({
            request: requestData, successCallback, failCallback, ...otherData
        }) {
            const request = requestData || otherData
            const params = JSON.stringify(request)
            fetchRequest(params, (response) => {
                const { data, meta } = response
                return successCallback(data, meta.totalRows)
            })
        },
    }

    // Set data Grid
    if (ROW_MODEL_TYPE === GridEntity.sidesData.INFINITE) grid.gridApi.setDatasource(dataSource)
    else if (ROW_MODEL_TYPE === GridEntity.sidesData.SERVER_SIDE) grid.gridApi.setServerSideDatasource(dataSource)
    else fetchRequest({}, ({ data = [] }) => grid.gridApi.setRowData(data))
}

/**
 * Normalize columns
*/
const COL_RULES = {
    disabled: ({ col }) => col.editable === false,
}

const colStyles = (col) => {
    const disabled = COL_RULES.disabled({ col })

    return {
        headerClass: [
            'ag-header-custom',
            ...(disabled ? ['ag-header-disabled'] : []),
        ],
        cellClass: [
            'ag-cell-custom',
            ...(disabled ? ['ag-cell-disabled'] : []),
        ],
    }
}

const normalizeColumns = cols => cols.map(col => ({
    ...col,
    ...(col.children ? { children: normalizeColumns(col.children) } : {}),
    ...colStyles(col),
}))

export default {
    Config: DATAGRID_CONFIG,
    Handler,
    normalizeColumns,
    compareValues: GridEntity.compareValues,
}
